import { Building2Icon, CalendarDaysIcon, CheckIcon, ClockIcon, MapPinIcon, PhoneIcon, Plus, TrashIcon, VideoIcon } from 'assets/icons';
import ActivityTimeline from 'components/activity/ActivityTimeline';
import EmployeeAvatar from 'components/assign/EmployeeAvatar';
import Badge from 'components/badge/badge';
import Button from 'components/button';
import { cardIconButtonClass } from 'components/card/PanelCard';
import CommentComposer from 'components/comments/CommentComposer';
import CommentThread from 'components/comments/CommentThread';
import useCommentThread from 'components/comments/useCommentThread';
import IconTooltip from 'components/iconTooltip/IconTooltip';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverFooter from 'components/SlideOver/component/footer';
import SlideOverHeader from 'components/SlideOver/component/header';
import useSlideOver from 'components/SlideOver/useSlideOver';
import AutosaveTextarea from 'components/styledInput/AutosaveTextarea';
import LineTabs from 'components/tabs/LineTabs';
import TaskSuggestions from 'components/tasks/suggestions/TaskSuggestions';
import { EVENT_CATEGORIES, eventCategoryOf, eventCategoryText } from 'constants/eventCategories';
import { addDays, clockLabel, dayLabel, isoDay } from 'helper/timeLabels';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import { useNotifyPlanningChanged } from 'hooks/usePlanningChanged';
import useText from 'hooks/useText';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
// The planning stays on the demo until its back exists: its tasks and team
// must match its demo appointments.
import { crmMockApi } from 'server/crm/crmApi';

// A real company of Jobbiz has a page; the demo's own do not.
const isRealId = (id) => /^[0-9a-f]{24}$/i.test(id ?? '');

const whenOf = (start, end, now, text) => {
  let day = dayLabel(start);
  if (isoDay(start) === isoDay(now)) day = text('today');
  else if (isoDay(start) === isoDay(addDays(now, 1))) day = text('tomorrow');
  else if (isoDay(start) === isoDay(addDays(now, -1))) day = text('yesterday');
  return `${day}, ${clockLabel(start)} – ${clockLabel(end)}`;
};

/**
 * The panel of an appointment (slide-over `get_appointment`, its id in
 * `objID`), as on the prototype: its category, when, whether it was held,
 * the person met, where, its report written in place; its history and its
 * comments. Held or not once over; marked done or called before. Every change
 * tells the planning (usePlanningChanged).
 */
const AppointmentPanel = () => {
  const { objID } = UseContext();
  const id = typeof objID === 'string' ? objID : null;
  const { text, language } = useText('appointment');
  const { call, me } = useCrmContext();
  const { toggleSlideOver } = useSlideOver();
  const navigate = useNavigate();
  const location = useLocation();
  const notify = useNotifyPlanningChanged();
  const [item, setItem] = useState(null);
  const [team, setTeam] = useState([]);
  const [tab, setTab] = useState('detail');
  // The report as typed (not yet saved), for the suggestions under it.
  const [report, setReport] = useState('');

  const load = useCallback(() => call(crmMockApi.getAppointment, { id }).then(setItem, () => setItem(null)), [call, id]);
  useEffect(() => {
    if (!id || !me) return;
    setItem(null);
    setTab('detail');
    load();
    call(crmMockApi.getTeam).then(setTeam, () => setTeam([]));
  }, [call, load, id, me]);

  useEffect(() => setReport(item?.outcome ?? ''), [item?.id, item?.outcome]);

  const thread = useCommentThread({
    key: id,
    load: () => call(crmMockApi.listComments, { taskId: `a:${id}` }),
    add: (body, mentions) => call(crmMockApi.addComment, { taskId: `a:${id}`, body, mentions }),
    people: team,
  });

  const close = () => toggleSlideOver();
  if (!item) {
    return (
      <>
        <SlideOverHeader hide={close} closeLabel={text('close')} />
        <SlideOverBody>
          <div className="mx-auto mt-16 h-6 w-6 animate-spin rounded-full border-2 border-yellow-500 border-t-transparent" />
        </SlideOverBody>
      </>
    );
  }

  const now = new Date();
  const start = new Date(item.startsAt);
  const end = new Date(item.endsAt);
  const company = item.company;
  const category = eventCategoryOf(item.category);
  const contact = item.contactName || company?.contactName || '';
  const role = contact && contact === company?.contactName ? company?.contactRole : '';
  const phone = company?.phone ?? '';
  const late = end < now;
  const act = async (run, message) => {
    try {
      await run();
      toast.success(message);
      notify();
      close();
    } catch (error) {
      toast.error(error.message);
    }
  };
  const mark = (status, message) => act(() => call(crmMockApi.updateAppointment, { id: item.id, fields: { status } }), message);
  const saveReport = (outcome) =>
    call(crmMockApi.updateAppointment, { id: item.id, fields: { outcome } }).then(
      () => {
        toast.success(text('reportSaved'));
        notify();
        return true;
      },
      (error) => {
        toast.error(error.message);
        return false;
      },
    );
  const [firstName, ...rest] = contact.split(' ');
  const history = item.history.map((event) => ({
    id: event.id,
    icon: event.kind === 'created' ? <Plus wh={15} color="currentColor" /> : <CheckIcon wh={15} color="currentColor" />,
    text: text(`history_${event.kind}`),
    at: new Date(event.at),
  }));

  let actions;
  if (item.status !== 'planned') {
    actions = (
      <Button color="SECONDARY" size="SM" onClick={() => mark('planned', text('reopened'))}>
        {text('reopen')}
      </Button>
    );
  } else if (late) {
    actions = (
      <>
        <Button color="SECONDARY" size="SM" onClick={() => mark('no_show', text('noShowDone'))}>
          {text('noShow')}
        </Button>
        <Button color="PRIMARY" size="SM" onClick={() => mark('done', text('heldDone'))}>
          {text('held')}
        </Button>
      </>
    );
  } else {
    actions = (
      <>
        <Button color={phone ? 'SECONDARY' : 'PRIMARY'} size="SM" onClick={() => mark('done', text('heldDone'))}>
          {text('markDone')}
        </Button>
        {phone && (
          <Button color="PRIMARY" size="SM" onClick={() => window.location.assign(`tel:${phone.replace(/\s/g, '')}`)}>
            <PhoneIcon wh={16} color="currentColor" />
            {text('call')}
          </Button>
        )}
      </>
    );
  }

  return (
    <>
      <SlideOverHeader hide={close} closeLabel={text('close')}>
        {category === 'interne' ? item.title : company?.name || item.title}
      </SlideOverHeader>
      <div className="flex-shrink-0 px-4 md:px-6">
        <LineTabs
          activeTab={tab}
          onChange={setTab}
          tabs={[
            { id: 'detail', label: text('tabDetail') },
            { id: 'activity', label: text('tabActivity') },
            { id: 'comments', label: text('tabComments') },
          ]}
        />
      </div>
      <SlideOverBody className="flex flex-col">
        {tab === 'comments' && <CommentThread comments={thread.comments} people={team} />}
        {tab === 'activity' && <ActivityTimeline items={history} />}
        {tab === 'detail' && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-lg px-3 py-1 text-xs font-medium ${EVENT_CATEGORIES[category].tag}`}>
                {eventCategoryText(language, category, 'label')}
              </span>
              <Badge type={late && item.status === 'planned' ? 'ALERT' : 'PRIMARY'}>
                <ClockIcon wh={14} color="currentColor" />
                {whenOf(start, end, now, text)}
              </Badge>
              {item.status === 'done' && <Badge type="VALID">{text('statusDone')}</Badge>}
              {item.status === 'no_show' && <Badge type="ALERT">{text('statusNoShow')}</Badge>}
            </div>
            {(contact || company) && (
              <div className="flex items-center gap-3 rounded-xl border border-gray-200 p-3">
                <EmployeeAvatar employee={{ firstname: firstName || company?.name, lastname: rest.join(' ') }} size={40} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900">{contact || company?.name}</p>
                  <p className="text-xs text-gray-600">{[role, phone].filter(Boolean).join(' · ')}</p>
                </div>
                {company && isRealId(company.id) && (
                  <Button color="SECONDARY" size="SM" onClick={() => navigate(`/company/${company.id}`)}>
                    <Building2Icon wh={16} color="currentColor" />
                    {text('record')}
                  </Button>
                )}
              </div>
            )}
            {(item.location || item.mode) && (
              <div className="flex flex-col gap-2 text-sm text-gray-700">
                {item.location && (
                  <p className="flex items-center gap-2">
                    <span className="flex shrink-0 text-gray-400">
                      <MapPinIcon wh={16} color="currentColor" />
                    </span>
                    {item.location}
                  </p>
                )}
                {item.mode && (
                  <p className="flex items-center gap-2">
                    <span className="flex shrink-0 text-gray-400">
                      {/visio/i.test(item.mode) ? <VideoIcon wh={16} color="currentColor" /> : <Building2Icon wh={16} color="currentColor" />}
                    </span>
                    {item.mode}
                  </p>
                )}
              </div>
            )}
            <div onChange={(event) => setReport(event.target.value)}>
              <AutosaveTextarea
                key={item.id}
                label={text('report')}
                placeholder={text('reportPlaceholder')}
                initial={item.outcome ?? ''}
                onSave={saveReport}
              />
            </div>
            <TaskSuggestions
              key={item.id}
              note={report}
              origin="rendez-vous"
              companyId={company?.id}
              contactName={contact || undefined}
              contactRole={role}
              contactPhone={phone}
              contactEmail={company?.email}
              companyPhone={phone}
              companyEmail={company?.email}
              enabled
            />
            {item.description && (
              <div>
                <p className="text-sm font-medium text-gray-700">{text('note')}</p>
                <p className="mt-1 whitespace-pre-line text-sm text-gray-600">{item.description}</p>
              </div>
            )}
          </div>
        )}
      </SlideOverBody>
      {tab === 'comments' && (
        <div className="flex-shrink-0 border-t border-gray-100 px-6 py-4">
          <CommentComposer people={team} meId={me?.id} onSend={thread.send} />
        </div>
      )}
      <div className="flex-shrink-0 border-t border-gray-100">
        <SlideOverFooter>
          <div className="mr-auto flex items-center gap-2">
            {!location.pathname.startsWith('/planning') && (
              <IconTooltip label={text('seeInPlanning')}>
                <button
                  type="button"
                  aria-label={text('seeInPlanning')}
                  onClick={() => {
                    close();
                    navigate(`/planning?date=${isoDay(start)}`);
                  }}
                  className={cardIconButtonClass}
                >
                  <CalendarDaysIcon wh={18} color="#374151" />
                </button>
              </IconTooltip>
            )}
            <IconTooltip label={text(item.visit ? 'unplace' : 'delete')}>
              <button
                type="button"
                aria-label={text(item.visit ? 'unplace' : 'delete')}
                onClick={() => act(() => call(crmMockApi.removeAppointment, { id: item.id }), text(item.visit ? 'unplaced' : 'deleted'))}
                className={cardIconButtonClass}
              >
                <TrashIcon wh={18} color="#374151" />
              </button>
            </IconTooltip>
          </div>
          {actions}
        </SlideOverFooter>
      </div>
    </>
  );
};

export default AppointmentPanel;
